// External storage selection rules shared by every page that creates observations (#1474).
//
// The pages flatten the /configs/available response into one list tagged with `_scope`.
//
// - Required, checked and locked: the selected project's own configs and the user's personal
//   configs. The server adds them to every new observation (services/externalStorage/
//   requiredStorage.ts), so the page shows where the data will go.
// - Selected by default (#1454): organization configs an org admin flagged `default_selected`
//   start checked, and a member may uncheck them.
// - Everything else is opt-in.
//
// When editing an existing observation only the project's configs are required: the server
// applies the rule on create, and an update must not rewrite the observation's destinations.

/** The /configs/available response as one list, each config tagged with its scope. */
export const flattenAvailableStorage = (data) => {
  const all = [];
  for (const config of data?.personal || []) {
    all.push({ ...config, _scope: 'personal', _scopeLabel: 'Personal' });
  }
  for (const [orgId, group] of Object.entries(data?.organization || {})) {
    for (const config of group.configs || []) {
      all.push({ ...config, _scope: 'organization', _scopeLabel: group.name, _scopeId: orgId });
    }
  }
  for (const [projId, group] of Object.entries(data?.project || {})) {
    for (const config of group.configs || []) {
      all.push({ ...config, _scope: 'project', _scopeLabel: group.name, _scopeId: projId });
    }
  }
  return all;
};

export const defaultSelectedStorageIds = (configs) =>
  (Array.isArray(configs) ? configs : [])
    .filter(c => c && c._scope === 'organization' && c.default_selected === true)
    .map(c => c._id);

// Adds the default-selected ids to the current selection without duplicating any, and returns
// the same array when there is nothing to add so a React state update can bail out.
export const withDefaultSelectedStorage = (selectedIds, configs) => {
  const current = Array.isArray(selectedIds) ? selectedIds : [];
  const missing = defaultSelectedStorageIds(configs).filter(id => !current.includes(id));
  return missing.length === 0 ? current : [...current, ...missing];
};

export const requiredStorageIds = (configs, projectId, { isEditing = false } = {}) =>
  (Array.isArray(configs) ? configs : [])
    .filter(c => {
      if (!c) return false;
      if (c._scope === 'project') return Boolean(projectId) && String(c._scopeId) === String(projectId);
      return !isEditing && c._scope === 'personal';
    })
    .map(c => c._id);
